import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSignOutAlt, FaCar, FaCheckCircle, FaTimesCircle, FaEnvelope, FaStar, FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight, FaFileExport, FaTimes } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { clearAdminSession, vehicleAPI, resolveMediaUrl } from '../../services/api';
import ConfirmModal from '../../components/admin/ConfirmModal';
import SearchBar from '../../components/admin/SearchBar';
import FilterBar from '../../components/admin/FilterBar';
import adminLogo from '../../images/logo.png';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    featured: 'all',
    year: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleAPI.getAll();
      if (response.data.success) {
        setVehicles(response.data.data);
      }
    } catch (error) {
      setError('Failed to fetch vehicles');
      toast.error('Failed to load vehicles');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await vehicleAPI.delete(id);
      if (response.data.success) {
        setVehicles(vehicles.filter(v => v.id !== id));
        setConfirmDelete(null);
        toast.success('Vehicle deleted successfully!');
      }
    } catch (error) {
      setError('Failed to delete vehicle');
      toast.error('Failed to delete vehicle');
      console.error('Delete error:', error);
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    navigate('/admin/login');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      featured: 'all',
      year: 'all'
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort className="sort-icon" />;
    }
    return sortConfig.direction === 'asc' ?
      <FaSortUp className="sort-icon active" /> :
      <FaSortDown className="sort-icon active" />;
  };

  const handleExport = () => {
    try {
      const escapeCsvValue = (value) => {
        const normalized = value ?? '';
        const stringValue = String(normalized);
        if (/[",\n]/.test(stringValue)) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const csvContent = [
        ['Make', 'Model', 'Year', 'Price', 'Mileage', 'Status', 'Featured'].join(','),
        ...filteredVehicles.map(v =>
          [
            v.make,
            v.model,
            v.year,
            v.price,
            v.mileage,
            v.status,
            v.featured ? 'Yes' : 'No'
          ].map(escapeCsvValue).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vehicles_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Vehicles exported successfully!');
    } catch (error) {
      toast.error('Failed to export vehicles');
      console.error('Export error:', error);
    }
  };

  const handleStatCardClick = (filterType, filterValue) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: filterValue
    }));
    setCurrentPage(1);
  };

  const filteredVehicles = useMemo(() => {
    let filtered = [...vehicles];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(vehicle => {
        const searchLower = searchTerm.toLowerCase();
        return (
          vehicle.make.toLowerCase().includes(searchLower) ||
          vehicle.model.toLowerCase().includes(searchLower) ||
          vehicle.year.toString().includes(searchLower) ||
          vehicle.price.toString().includes(searchLower)
        );
      });
    }

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(v => v.status === filters.status);
    }

    // Apply featured filter
    if (filters.featured !== 'all') {
      filtered = filtered.filter(v => {
        if (filters.featured === 'yes') return v.featured === true;
        if (filters.featured === 'no') return v.featured === false;
        return true;
      });
    }

    // Apply year filter
    if (filters.year !== 'all') {
      filtered = filtered.filter(v => {
        if (filters.year === '2019') return v.year <= 2019;
        return v.year.toString() === filters.year;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties (e.g., "make model")
        if (sortConfig.key === 'name') {
          aValue = `${a.make} ${a.model}`;
          bValue = `${b.make} ${b.model}`;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [vehicles, searchTerm, filters, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(filteredVehicles.length / itemsPerPage);
  const paginatedVehicles = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredVehicles.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredVehicles, currentPage]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <div className="admin-header-left">
            <img src={adminLogo} alt="Kariyapperuma Car Sales" className="admin-header-logo" />
            <div>
              <h1>Admin Dashboard</h1>
              <p>Kariyapperuma Car Sales</p>
            </div>
          </div>
          <div className="admin-header-right">
            <div className="skeleton-line" style={{ width: '120px', height: '40px' }}></div>
            <div className="skeleton-line" style={{ width: '180px', height: '40px' }}></div>
            <div className="skeleton-line" style={{ width: '100px', height: '40px' }}></div>
          </div>
        </div>

        <div className="dashboard-content">
          <div className="skeleton-stats">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-icon"></div>
                <div className="skeleton-text">
                  <div className="skeleton-line" style={{ width: '70%' }}></div>
                  <div className="skeleton-line large"></div>
                </div>
              </div>
            ))}
          </div>

          <div className="skeleton-table">
            <div className="skeleton-line" style={{ width: '200px', height: '30px', marginBottom: '2rem' }}></div>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="skeleton-table-row">
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-left">
          <img src={adminLogo} alt="Kariyapperuma Car Sales" className="admin-header-logo" />
          <div>
            <h1>Admin Dashboard</h1>
            <p>Kariyapperuma Car Sales</p>
          </div>
        </div>
        <div className="admin-header-right">
          <motion.button
            className="btn-add"
            onClick={() => navigate('/admin/vehicle/new')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPlus /> Add Vehicle
          </motion.button>
          <motion.button
            className="btn-contacts"
            onClick={() => navigate('/admin/contacts')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaEnvelope /> Contact Submissions
          </motion.button>
          <motion.button
            className="btn-logout"
            onClick={handleLogout}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaSignOutAlt /> Logout
          </motion.button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <FaTimesCircle /> {error}
        </div>
      )}

      <div className="dashboard-content">
        <div className="dashboard-stats">
          <motion.div
            className="stat-card stat-card-blue"
            onClick={() => handleClearFilters()}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <div className="stat-icon stat-icon-blue">
              <FaCar />
            </div>
            <div className="stat-content">
              <h3>Total Vehicles</h3>
              <p className="stat-number">{vehicles.length}</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card stat-card-green"
            onClick={() => handleStatCardClick('status', 'available')}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <div className="stat-icon stat-icon-green">
              <FaCheckCircle />
            </div>
            <div className="stat-content">
              <h3>Available</h3>
              <p className="stat-number">{vehicles.filter(v => v.status === 'available').length}</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card stat-card-red"
            onClick={() => handleStatCardClick('status', 'sold')}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <div className="stat-icon stat-icon-red">
              <FaTimesCircle />
            </div>
            <div className="stat-content">
              <h3>Sold</h3>
              <p className="stat-number">{vehicles.filter(v => v.status === 'sold').length}</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card stat-card-yellow"
            onClick={() => handleStatCardClick('featured', 'yes')}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <div className="stat-icon stat-icon-yellow">
              <FaStar />
            </div>
            <div className="stat-content">
              <h3>Featured</h3>
              <p className="stat-number">{vehicles.filter(v => v.featured).length}</p>
            </div>
          </motion.div>
        </div>

        <div className="vehicles-table-container">
          <div className="table-header">
            <h2>All Vehicles</h2>
            <div className="table-header-actions">
              <SearchBar onSearch={setSearchTerm} placeholder="Search vehicles..." />
              <motion.button
                className="btn-export"
                onClick={handleExport}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={filteredVehicles.length === 0}
              >
                <FaFileExport /> Export
              </motion.button>
            </div>
          </div>

          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />

          {filteredVehicles.length === 0 && vehicles.length > 0 ? (
            <div className="no-vehicles">
              <FaCar />
              <p>No vehicles match your search or filters.</p>
              <button onClick={handleClearFilters} className="btn-add-first">
                Clear Filters
              </button>
            </div>
          ) : vehicles.length === 0 ? (
            <div className="no-vehicles">
              <FaCar />
              <p>No vehicles found. Add your first vehicle!</p>
              <button onClick={() => navigate('/admin/vehicle/new')} className="btn-add-first">
                <FaPlus /> Add Vehicle
              </button>
            </div>
          ) : (
            <>
              <div className="table-scroll-wrapper">
                <table className="vehicles-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th className="sortable" onClick={() => handleSort('name')}>
                      Vehicle {getSortIcon('name')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('year')}>
                      Year {getSortIcon('year')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('price')}>
                      Price {getSortIcon('price')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('mileage')}>
                      Mileage {getSortIcon('mileage')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('status')}>
                      Status {getSortIcon('status')}
                    </th>
                    <th>Featured</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedVehicles.map((vehicle, index) => (
                    <motion.tr
                      key={vehicle.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <td>
                        <div
                          className="vehicle-image"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (vehicle.images && vehicle.images.length > 0) {
                              setSelectedImage(resolveMediaUrl(vehicle.images[0]));
                            }
                          }}
                        >
                          {vehicle.images && vehicle.images.length > 0 ? (
                            <img
                              src={resolveMediaUrl(vehicle.images[0])}
                              alt={`${vehicle.make} ${vehicle.model}`}
                            />
                          ) : (
                            <div className="no-image"><FaCar /></div>
                          )}
                        </div>
                      </td>
                      <td>
                        <strong>{vehicle.make} {vehicle.model}</strong>
                        <br />
                        <small>{vehicle.bodyType}</small>
                      </td>
                      <td>{vehicle.year}</td>
                      <td className="price">{formatPrice(vehicle.price)}</td>
                      <td>{vehicle.mileage.toLocaleString()} km</td>
                      <td>
                        <span className={`status-badge status-${vehicle.status}`}>
                          {vehicle.status}
                        </span>
                      </td>
                      <td className="text-center">
                        {vehicle.featured ? (
                          <FaCheckCircle className="icon-success" />
                        ) : (
                          <FaTimesCircle className="icon-muted" />
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <motion.button
                            className="btn-edit"
                            onClick={() => navigate(`/admin/vehicle/edit/${vehicle.id}`)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Edit"
                          >
                            <FaEdit />
                          </motion.button>
                          <motion.button
                            className="btn-delete"
                            onClick={() => setConfirmDelete(vehicle)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Delete"
                          >
                            <FaTrash />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft /> Previous
                  </button>

                  <div className="pagination-info">
                    Page {currentPage} of {totalPages}
                    <span className="pagination-count">
                      ({filteredVehicles.length} total)
                    </span>
                  </div>

                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next <FaChevronRight />
                  </button>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => handleDelete(confirmDelete.id)}
        title="Delete Vehicle"
        message={`Are you sure you want to delete ${confirmDelete?.make} ${confirmDelete?.model}? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {selectedImage && (
        <motion.div
          className="image-modal-overlay"
          onClick={() => setSelectedImage(null)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setSelectedImage(null)}>
              <FaTimes />
            </button>
            <img src={selectedImage} alt="Vehicle" />
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminDashboard;
